import { useState } from "react";

import SectionCard from "@buildpy/ui/section-card";
import Money from "@/components/money";
import StatCard from "@/components/stat-card";
import { Input } from "@buildpy/ui/input";
import { EMPTY_MARK, formatNumber, formatRate, formatWon, parseMoney } from "@buildpy/core/lib/format";
import { ohouseCost, parseCount, parsePercent } from "@buildpy/core/modules/tools/ohouse-cost";

const moneyText = (text: string): string => (text.trim() === "" ? "" : formatNumber(parseMoney(text)));
const rateOr = (rate: number | null): string => (rate === null ? EMPTY_MARK : formatRate(rate));

// 오늘의집 광고비 계산 — 숫자는 이 화면에만 있고 어디에도 보내지 않는다.
const OhouseCalculator = () => {
  const [ad, setAd] = useState("");
  const [contracts, setContracts] = useState("");
  const [avg, setAvg] = useState("");
  const [margin, setMargin] = useState("");
  const result = ohouseCost({
    monthlyAd: parseMoney(ad),
    monthlyContracts: parseCount(contracts),
    avgContractAmount: parseMoney(avg),
    marginRate: parsePercent(margin),
  });

  return (
    <div className="flex flex-col gap-4">
      <SectionCard padding="md" title="사장님 숫자">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Input
            id="ohouse-ad"
            inputMode="numeric"
            label="월 광고비 (원)"
            name="ohouse-ad"
            placeholder="예: 1,200,000"
            value={ad}
            onChange={(e) => setAd(moneyText(e.target.value))}
          />
          <Input
            id="ohouse-contracts"
            inputMode="decimal"
            label="월 계약 건수 (오늘의집으로 온 것)"
            name="ohouse-contracts"
            placeholder="예: 2"
            value={contracts}
            onChange={(e) => setContracts(e.target.value)}
          />
          <Input
            id="ohouse-avg"
            inputMode="numeric"
            label="평균 계약금액 (원)"
            name="ohouse-avg"
            placeholder="예: 30,000,000"
            value={avg}
            onChange={(e) => setAvg(moneyText(e.target.value))}
          />
          <Input
            id="ohouse-margin"
            inputMode="decimal"
            label="마진율 (%)"
            name="ohouse-margin"
            placeholder="예: 15"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
          />
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="ohouse-result">
        <StatCard
          hint="월 광고비 ÷ 월 계약 건수"
          title="계약 1건에 든 광고비"
          value={<Money value={result.costPerContract} />}
        />
        <StatCard
          hint={
            result.shareOfProfit === null
              ? "계약금액 대비"
              : `계약금액 대비 · 1건 이익(${formatWon(result.profitPerContract)})의 ${formatRate(result.shareOfProfit)}`
          }
          title="계약금액의 몇 %"
          value={rateOr(result.shareOfContract)}
        />
        <StatCard
          hint={
            result.adFreeYearly === null
              ? "광고비는 그대로 나갑니다 · 한 달 기준"
              : `광고비는 그대로 나갑니다 · 한 달에 1건씩이면 1년 ${formatWon(result.adFreeYearly)}어치`
          }
          title="자사 채널로 1건이 오면 — 광고비 없이 받은 계약"
          value={<Money value={result.adFreeMonthly} />}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        월 광고비와 계약 건수를 넣으면 계산됩니다. 오늘의집은 그대로 쓰세요 — 자사 홈페이지·소개로 오는 계약은 광고비 없이 받은 계약이니, 놓치지 않게 기록해 두세요.
      </p>
    </div>
  );
};

export default OhouseCalculator;
